import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSnackbar } from '../../contexts/SnackbarContext';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import './ClientRegistration.css';
import appLogo from '../../shared/assets/logopng.png';
import { clientService } from './services/clientService';
import MapPickerModal from './MapPickerModal';
import CountryPhoneInput from '../../shared/components/CountryPhoneInput';
import MainLoader from '../../shared/components/MainLoader';
import { IoMailOpenOutline } from 'react-icons/io5';


const IconUser = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
  </svg>
);
const IconPhone = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.4 2 2 0 0 1 3.6 1.22h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L7.91 8.8a16 16 0 0 0 6.29 6.29l.95-.95a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
  </svg>
);
const IconMapPin = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
  </svg>
);
const IconCrosshair = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/><line x1="22" y1="12" x2="18" y2="12"/><line x1="6" y1="12" x2="2" y2="12"/><line x1="12" y1="6" x2="12" y2="2"/><line x1="12" y1="22" x2="12" y2="18"/>
  </svg>
);
const IconNote = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14,2 14,8 20,8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/>
  </svg>
);
const IconArrow = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12,5 19,12 12,19"/>
  </svg>
);

const ClientRegistration = () => {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [dialCode, setDialCode] = useState('91');
  const [address, setAddress] = useState('');
  const [latLong, setLatLong] = useState('');
  const [note, setNote] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const [isLocating, setIsLocating] = useState(false);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const { executeRecaptcha } = useGoogleReCaptcha();
  const { showSnackbar } = useSnackbar();
  
  // Organization Context
  const { token } = useParams();
  const [orgInfo, setOrgInfo] = useState(null);
  const [isVerifying, setIsVerifying] = useState(true);
  const [verifyError, setVerifyError] = useState(null);

  const validateMobile = (p) => /^\d{7,14}$/.test(p.replace(/\D/g, ''));

  const validate = (fields = {}) => {
    const e = {};
    const n = fields.name ?? name;
    const p = fields.phone ?? phone;
    const em = fields.email ?? email;
    if (!n.trim()) e.name = 'Company name is required';
    else if (n.trim().length < 2) e.name = 'Must be at least 2 characters';
    else if (n.trim().length > 50) e.name = 'Must be 30 characters or less';
    if (!em.trim()) e.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em)) e.email = 'Enter a valid email address';
    if (!p) e.phone = 'Phone number is required';
    else if (!validateMobile(p)) e.phone = 'Enter a valid phone number (7–14 digits)';
    return e;
  };

  const handleBlur = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    setErrors(validate());
  };

  const handleMapConfirm = (lat, lng) => setLatLong(`${lat}, ${lng}`);

  useEffect(() => {
    const verifyOrg = async () => {
      if (!token) {
        setVerifyError('This registration page requires a valid partner link.');
        setIsVerifying(false);
        return;
      }

      try {
        const response = await clientService.verifyOrganization(token);
        if (response?.data?.errors) {
          throw new Error(response.data.errors);
        }
        const orgData = response.data?.data || response.data;
        console.log('orgInfo:', orgData);
        setOrgInfo(orgData);
      } catch (err) {
        setVerifyError(err?.message || 'Invalid or expired registration link.');
      } finally {
        setIsVerifying(false);
      }
    };

    verifyOrg();
  }, [token]);

  const handleSave = async () => {
    console.log('handleSave called');
    const allTouched = { name: true, email: true, phone: true };
    setTouched(allTouched);
    const validationErrors = validate();
    setErrors(validationErrors);
    console.log('validationErrors:', validationErrors);
    console.log('executeRecaptcha:', executeRecaptcha);
    console.log('orgInfo:', orgInfo);
    if (Object.keys(validationErrors).length > 0) return;
    if (!executeRecaptcha) { showSnackbar('reCAPTCHA not ready, please wait...', 'error'); return; }
    if (!orgInfo?.id) {
      showSnackbar('Organization context is missing. Please use a valid registration link.', 'error');
      return;
    }

    setIsLoading(true);
    try {
      console.log('calling executeRecaptcha...');
      const recaptchaToken = await executeRecaptcha('client_register');
      console.log('recaptchaToken:', recaptchaToken);
      const payload = {
        name,
        email,
        phone_number: phone,
dial_code: `+${dialCode}`,
        location: latLong,
        address: address,
        post_code: '',
        note,
        ouId: orgInfo?.id,
        reCaptcha: recaptchaToken,
      };
      const response = await clientService.registerClient(payload);
      if (response?.data?.success) {
        showSnackbar('Registration successful. We will contact you shortly.', 'success');
        navigate('/login');
      } else {
        showSnackbar(response?.data?.message || 'Registration failed', 'error');
      }
    } catch (error) {
      showSnackbar(error?.response?.data?.message || 'Something went wrong', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  if (isVerifying) {
    return (
      <div className="cr-root" style={{ alignItems: 'center', justifyContent: 'center' }}>
        <MainLoader text="Verifying link..." />
      </div>
    );
  }

  if (verifyError) {
    return (
      <div className="cr-root flex items-center justify-center bg-gray-50 h-screen">
        <div className="cr-card p-12 text-center shadow-xl max-w-sm">
          <div className="text-red-500 text-4xl mb-4">⚠️</div>
          <h2 className="text-xl font-bold mb-2">Registration Link Error</h2>
          <p className="text-gray-500 mb-6">{verifyError}</p>
          <button className="cr-btn-submit w-full" onClick={() => navigate('/login')}>Return to Login</button>
        </div>
      </div>
    );
  }

  const displayLogo = orgInfo?.logo_url || appLogo;
  const displayName = orgInfo?.name || 'TykVen';
  const displayTagline = orgInfo?.tag_line || 'Field service, reimagined.';

  return (
    <div className="cr-root">
      {showMap && <MapPickerModal onConfirm={handleMapConfirm} onClose={() => setShowMap(false)} />}
      {/* ── Left Panel ── */}
      <aside className="cr-left">
        <div className="cr-left-top">
          <div className="cr-logo-wrap">
            <img src={displayLogo} alt="TykVen" className="cr-logo-img" />
            <div>
              <div className="cr-logo-name">TykVen</div>
              <div className="cr-logo-sub">Client Portal</div>
            </div>
          </div>
          <div className="cr-tagline">
            {displayTagline.split(',').map((part, i) => (
              <React.Fragment key={i}>
                {i === 0 ? part + ',' : <><br /><span className="cr-tagline-accent">{part}</span></>}
              </React.Fragment>
            ))}
          </div>
          <p className="cr-tagline-desc">
            {/* {orgInfo?.description || 'Register your company and start managing field operations with clarity and speed.'} */}
          </p>
          <div className="cr-dots">
            {Array.from({ length: 25 }).map((_, i) => <div key={i} className="cr-dot" />)}
          </div>
        </div>

        <div className="cr-left-bottom">
          <p className="cr-divider-text">Already registered?</p>
          <button className="cr-login-btn" onClick={() => navigate('/login')}>
            Sign in to your account <IconArrow />
          </button>
        </div>
      </aside>

      {/* ── Right Panel ── */}
      <main className="cr-right">
        <div className="cr-card">
          <div className="cr-card-header">
            <div className="cr-step-label"><span className="cr-step-dot" /> New Account</div>
            <h1 className="cr-card-title">Register as Client</h1>
            <p className="cr-card-sub">Fill in your company details to get started.</p>
          </div>

          <form onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
            <div className="cr-grid">

              <div className="cr-field">
                <label className="cr-label">Company Name <span className="cr-req">*</span></label>
                <div className={`cr-input-wrap ${touched.name && errors.name ? 'cr-input-error' : ''}`}>
                  <span className="cr-input-icon-left"><IconUser /></span>
                  <input className="cr-input" type="text" value={name} maxLength={30} onChange={e => { setName(e.target.value); if (touched.name) setErrors(v => ({ ...v, name: !e.target.value.trim() ? 'Company name is required' : e.target.value.trim().length < 2 ? 'Must be at least 2 characters' : e.target.value.trim().length > 30 ? 'Must be 30 characters or less' : undefined })); }} onBlur={() => handleBlur('name')} placeholder="Enter your company name" />
                </div>
                {touched.name && errors.name && <span className="cr-error-msg">{errors.name}</span>}
              </div>

              <div className="cr-field">
                <label className="cr-label">Email <span className="cr-req">*</span></label>
                <div className={`cr-input-wrap ${touched.email && errors.email ? 'cr-input-error' : ''}`}>
                  <span className="cr-input-icon-left"><IoMailOpenOutline size={15} /></span>
                  <input className="cr-input" type="email" value={email} onChange={e => { setEmail(e.target.value); if (touched.email) setErrors(v => ({ ...v, email: !e.target.value.trim() ? 'Email is required' : !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e.target.value) ? 'Enter a valid email address' : undefined })); }} onBlur={() => handleBlur('email')} placeholder="Enter your email address" />
                </div>
                {touched.email && errors.email && <span className="cr-error-msg">{errors.email}</span>}
              </div>

              <div className="cr-field">
                <label className="cr-label">Phone <span className="cr-req">*</span></label>
                <CountryPhoneInput
                  country="in"
                  value={phone}
                  onChange={(val, countryData) => {
                    setPhone(val);
                    setDialCode(countryData.dialCode);
                    if (touched.phone) setErrors(v => ({ ...v, phone: !val ? 'Phone number is required' : !validateMobile(val) ? 'Enter a valid phone number (7–14 digits)' : undefined }));
                  }}
                  onBlur={() => handleBlur('phone')}
                  error={!!(touched.phone && errors.phone)}
                />
                {touched.phone && errors.phone && <span className="cr-error-msg">{errors.phone}</span>}
              </div>

              <div className="cr-field">
                <label className="cr-label">Address</label>
                <div className="cr-input-wrap">
                  <span className="cr-input-icon-left"><IconMapPin /></span>
                  <input className="cr-input" type="text" value={address} onChange={e => setAddress(e.target.value)} placeholder={isLocating ? 'Detecting location…' : 'Enter your address'} disabled={isLocating} />
                </div>
              </div>

              <div className="cr-field cr-span2">
                <label className="cr-label">Coordinates</label>
                <div className="cr-input-wrap">
                  <span className="cr-input-icon-left"><IconCrosshair /></span>
                  <input className="cr-input cr-input-coord" type="text" value={latLong} onChange={e => setLatLong(e.target.value)} placeholder="12.9716, 77.5946" disabled={isLocating} />
                  <button type="button" className="cr-map-btn" title="Pick on map" onClick={() => setShowMap(true)}>🗺</button>
                </div>
              </div>

              <div className="cr-field cr-span2">
                <label className="cr-label">Note</label>
                <div className="cr-input-wrap cr-textarea-wrap">
                  <span className="cr-input-icon-left cr-textarea-icon"><IconNote /></span>
                  <textarea className="cr-textarea" value={note} onChange={e => { if (e.target.value.length <= 300) setNote(e.target.value); }} placeholder="Any additional information…" maxLength={300} />
                </div>
                <span style={{ fontSize: 11, color: note.length >= 300 ? '#ef4444' : '#9ca3af', textAlign: 'right', display: 'block', marginTop: 4 }}>{note.length}/300</span>
              </div>

            </div>

            <div className="cr-actions">
              <button type="button" className="cr-btn-cancel" onClick={() => navigate('/login')}>
                Cancel
              </button>
              <button type="submit" className="cr-btn-submit" disabled={isLoading}>
                {isLoading ? <><div className="cr-spinner" /> Registering…</> : <>Register <IconArrow /></>}
              </button>
            </div>
          </form>
        </div>
      </main>

      {isLoading && (
        <div className="otp-overlay">
          <div className="otp-loader-box">
            <div className="otp-spinner"></div>
            <div className="otp-icon"><IoMailOpenOutline /></div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientRegistration;
